import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './pages/Login';
import DashboardPage from './features/dashboard/DashboardPage';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import ProtectedRoute from './features/auth/ProtectedRoute';
import './App.css';
import { AuthProvider } from './features/auth/AuthProvider';
import Categories from './features/categories/Categories';
import Pagination from './components/common/pagination';
import Products from './features/products/ProductTree';
import CreateProducts from './features/products/CreateProducts';
import ProductDetailPage from './features/products/ProductDetailPage';
import EditProducts from './features/products/EditProduct';
import MacroFamilyList from './features/macrofamily/MacrofamilyList';
import Industries from './features/industries/Industries';
import DistributorsList from './features/distributors/DistributorsList';
import AddDistributors from './features/distributors/AddDistributors';
import MultimediaList from './features/mediauploads/MultimediaList';
import MultiMediaDetailPage from './features/mediauploads/MultiMediaDetailPage';
import AddMultimedia from './features/mediauploads/AddMultimedia';
import EditDistributors from './features/distributors/EditDistributors';
import { ViewDistributor } from './features/distributors/ViewDistributor';
import AdminUserList from './features/adminusers/AdminUserList';
import Tags from './features/tags/Tags';
import RolesAndpermissions from './features/roles-and-permissions/RolesAndpermissions';
import EditMultimedia from './features/mediauploads/EditMultimedia';
import Modal from './components/common/modal';
import Graph from './components/common/graph';
import Country from './components/common/country';
import ProductList from './features/products/ProductList';
import AddProduct from './features/products/AddProduct';
import Settings from './features/settings/Settings';
import PushNotification from './features/notification/PushNotification';
import EditUsers from "./features/distributors/EditUsers"

/* =========================
   MAIN LAYOUT
========================= */
const MainLayout = () => {
  // Sidebar open state
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 900);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);

  /* ---------- TOGGLE ---------- */
  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  /* ---------- CLOSE SIDEBAR (PASSED TO SIDEBAR) ---------- */
  const closeSidebar = () => {
    if (window.innerWidth <= 900) {
      setSidebarOpen(false);
    }
  };

  /* ---------- HANDLE RESIZE ---------- */
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);

      if (mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ProtectedRoute>
      {/* SIDEBAR */}
      <Sidebar
        isOpen={sidebarOpen}
        closeSidebar={closeSidebar}
      />

      {/* OVERLAY (MOBILE ONLY) */}
      {sidebarOpen && isMobile && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* MAIN CONTENT */}
      <div className={`main-wrapper ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        <Header onToggleSidebar={toggleSidebar} />

        <main className="main-content">
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/categories" element={<ProtectedRoute requiredResource="category"><Categories /></ProtectedRoute>} />
            <Route path="/common/pagination" element={<Pagination />} />
            <Route path="/common/graph" element={<Graph />} />
            <Route path="/products" element={<ProtectedRoute requiredResource="Product"><Products /></ProtectedRoute>} />
            <Route path="/products/AddProduct" element={<ProtectedRoute requiredResource="Product"><AddProduct /></ProtectedRoute>} />
            <Route path="/products/CreateProducts" element={<ProtectedRoute requiredResource="Product"><CreateProducts /></ProtectedRoute>} />
            <Route path="/products/:productName" element={<ProtectedRoute requiredResource="Product"><ProductDetailPage /></ProtectedRoute>} />
            <Route path="/products/view/:id" element={<ProtectedRoute requiredResource="Product"><ProductDetailPage /></ProtectedRoute>} />
            <Route path="/products/EditProducts/:id" element={<ProtectedRoute requiredResource="Product"><EditProducts /></ProtectedRoute>} />
            <Route path="/macrofamily" element={<ProtectedRoute requiredResource="macrofamily"><MacroFamilyList /></ProtectedRoute>} />
            <Route path="/industries" element={<ProtectedRoute requiredResource="Industries"><Industries /></ProtectedRoute>} />
            <Route path="/distributorsList" element={<ProtectedRoute requiredResource="Distributors"><DistributorsList /></ProtectedRoute>} />
            <Route path="/distributors/AddDistributors" element={<ProtectedRoute requiredResource="Distributors"><AddDistributors /></ProtectedRoute>} />
            <Route path="/mediauploads" element={<ProtectedRoute requiredResource="MultiMedia"><MultimediaList /></ProtectedRoute>} />
            <Route path="/mediauploads/:id" element={<ProtectedRoute requiredResource="MultiMedia"><MultiMediaDetailPage /></ProtectedRoute>} />
            <Route path="/mediauploads/AddMultimedia" element={<ProtectedRoute requiredResource="MultiMedia"><AddMultimedia /></ProtectedRoute>} />
            <Route path="/distributors/EditDistributors" element={<ProtectedRoute requiredResource="Distributors"><EditDistributors /></ProtectedRoute>} />
            <Route path="/distributors/view/:id" element={<ProtectedRoute requiredResource="Distributors"><ViewDistributor /></ProtectedRoute>} />
            <Route path="/distributors/EditUsers/:id" element={<ProtectedRoute requiredResource="Distributors"><EditUsers /></ProtectedRoute>} />
            <Route path="/AdminUserList" element={<ProtectedRoute requiredResource="AdminUsers"><AdminUserList /></ProtectedRoute>} />
            <Route path="/tags" element={<ProtectedRoute requiredResource="tags"><Tags /></ProtectedRoute>} />
            <Route path="/roles-and-permissions" element={<ProtectedRoute requiredResource="Roles & permissions"><RolesAndpermissions /></ProtectedRoute>} />
            <Route path="/mediauploads/EditMultimedia/:id" element={<ProtectedRoute requiredResource="MultiMedia"><EditMultimedia /></ProtectedRoute>} />
            <Route path="/common/modal" element={<Modal />} />
            <Route path="/common/country" element={<Country />} />
            <Route path="products/ProductList" element={<ProtectedRoute requiredResource="Product"><ProductList /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute requiredResource="Settings"><Settings /></ProtectedRoute>} />
            <Route path="/notification" element={<PushNotification />} />
          </Routes>
        </main>
      </div>
    </ProtectedRoute>
  );
};

/* =========================
   APP ROOT
========================= */
export default function App() {
  return (
    <AuthProvider>
      {/* Toastify container for all pages */}
      <ToastContainer position="top-right" autoClose={3000} style={{ fontSize: '1rem', zIndex: 99999 }} />
      {/* (Optional) Keep hot-toast Toaster if you use hot-toast elsewhere */}
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 99999 }}
        toastOptions={{
          duration: 3000,
          style: { fontSize: '1rem' },
        }}
      />
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={<MainLayout />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}